import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDebateStore } from "../../store/debateStore";
import type {
  ModeratorSummonEntry,
  SubagentResultEntry,
} from "../../store/slices/qualitySlice";
import { Card } from "../common/Card";
import { Skeleton } from "../common/Skeleton";
import { CoverageLiveBar } from "../verdict/CoveragePanel";
import { InvestigationLiveBar } from "../verdict/InvestigationPanel";
import { ModeratorSummonBand } from "./ModeratorSummonBand";
import { SubagentResultCard } from "./SubagentResultCard";
import { TranscriptTurn } from "./TranscriptTurn";
import { useSessionPersonaFeedbackLoader } from "../../hooks/useSessionPersonaFeedback";
import { t } from "../../i18n";

const STICKY_THRESHOLD_PX = 96;

/* ─────────────────────────────────────────────────────────────────────────
 * Scroll-to-turn IPC contract
 * ---------------------------------------------------------------------
 * The present-mode `BigSpeakerStage` portrait is click-to-locate: a tap
 * on the active speaker should jump the right-rail transcript to that
 * speaker's current turn and visually flash it. To avoid coupling the
 * present-mode subtree to transcript-internal state (and to dodge a
 * Zustand `scrollTargetTurnId` field that would force every transcript
 * consumer to re-render on click), we ride a single browser-level
 * `CustomEvent`. The contract:
 *
 *   • Producer  : `dispatchScrollToTurn(turnId)` (PresentModeShell.tsx).
 *   • Consumer  : the `useEffect` hook below; resolves the index in
 *                 `turnOrder` and calls `virtualizer.scrollToIndex` with
 *                 `align: "center"` so the target turn lands mid-rail
 *                 and is unambiguous to the eye.
 *   • Side-effect : `setHighlightTurnId(turnId)` for ~1.6 s so the
 *                   target turn pulses with an accent ring; the flag
 *                   then naturally unsets without the producer needing
 *                   to clear it.
 *   • Failure mode: turn id not in current `turnOrder` (e.g. stale
 *                   click after `resetStream`) → no-op; the
 *                   listener does NOT throw and does NOT log.
 *
 * Event name MUST stay in lock-step with `CONSENSUS_SCROLL_TURN_EVENT`
 * in `components/present/PresentModeShell.tsx`.
 */
const CONSENSUS_SCROLL_TURN_EVENT = "consensus:scroll-to-turn";

interface ScrollToTurnDetail {
  turnId: string;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Kalite Planı — transcript feed'i artık salt turn listesi değil.
 * `moderator.summon` bandları ve `subagent.result` kartları, turn'lerle
 * kronolojik olarak (startedAt / receivedAt) harmanlanıp AYNI
 * virtualizer'dan akar. Bu event'ler hiç gelmezse feed birebir eski
 * turn listesine indirgenir — davranış değişmez.
 * ──────────────────────────────────────────────────────────────────────── */
type FeedRow =
  | { kind: "turn"; key: string; turnId: string }
  | { kind: "summon"; key: string; summon: ModeratorSummonEntry }
  | { kind: "subagent"; key: string; entry: SubagentResultEntry };

function SkeletonTurn({ delay }: { delay: number }) {
  return (
    <div
      className="glass flex flex-col gap-3 border-l-[3px] border-l-line-soft/50 px-4 py-3.5 sm:px-5 sm:py-4 opacity-90"
      style={{ animationDelay: `${delay}ms` }}
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Skeleton width={36} height={36} rounded="full" />
          <div className="space-y-1.5">
            <Skeleton width={120} height={10} />
            <Skeleton width={70} height={8} />
          </div>
        </div>
        <Skeleton width={62} height={18} rounded="full" />
      </header>
      <div className="space-y-1.5">
        <Skeleton width="92%" height={10} />
        <Skeleton width="86%" height={10} />
        <Skeleton width="62%" height={10} />
      </div>
    </div>
  );
}

export function RealTimeTranscript() {
  const turnOrder = useDebateStore((s) => s.turnOrder);
  const turnsById = useDebateStore((s) => s.turnsById);
  const personas = useDebateStore((s) => s.personas);
  const currentTurnId = useDebateStore((s) => s.currentTurnId);
  const roundIndex = useDebateStore((s) => s.roundIndex);
  const maxRounds = useDebateStore((s) => s.maxRounds);
  // Kalite Planı akış öğeleri — event gelmediği sürece iki liste de
  // boştur ve feed salt turn'lerden oluşur.
  const summons = useDebateStore((s) => s.summons);
  const subagentResults = useDebateStore((s) => s.subagentResults);

  // Persona Kütüphanesi — hangi konuşmacıların kalıcı kimlik olduğunu ve
  // hangi turlara zaten oy verildiğini oturum başına TEK istekle yükle.
  // Tur bileşenleri bunu paylaşımlı store'dan okur.
  const sessionId = useDebateStore((s) => s.sessionId);
  useSessionPersonaFeedbackLoader(sessionId);

  const parentRef = useRef<HTMLDivElement | null>(null);
  const stickyRef = useRef<boolean>(true);
  // Transient highlight id — set when an external "scroll to turn" event
  // arrives, cleared automatically after the flash window. Used by
  // `TranscriptTurn` via the `isCurrent` style hook (we reuse the
  // existing accent treatment instead of inventing a third visual
  // state — keeps the design system DRY).
  const [highlightTurnId, setHighlightTurnId] = useState<string | null>(null);

  // Kronolojik birleşik feed. `turnsById` zaten her token'da değiştiği
  // (ve bu bileşen ona abone olduğu) için ek re-render maliyeti yok;
  // O(n log n) sort ~yüzlerce satırda ölçülemez. JS sort stabil —
  // eş-zamanlı kayıtlar ekleniş sırasını korur.
  const feedRows = useMemo<FeedRow[]>(() => {
    const stamped: { row: FeedRow; ts: number }[] = [];
    for (const id of turnOrder) {
      const turn = turnsById[id];
      if (!turn) continue;
      stamped.push({
        row: { kind: "turn", key: `turn-${id}`, turnId: id },
        ts: turn.startedAt,
      });
    }
    summons.forEach((summon, i) => {
      stamped.push({
        row: { kind: "summon", key: `summon-${summon.witnessId}-${i}`, summon },
        ts: summon.receivedAt,
      });
    });
    for (const entry of subagentResults) {
      stamped.push({
        row: { kind: "subagent", key: entry.id, entry },
        ts: entry.receivedAt,
      });
    }
    stamped.sort((a, b) => a.ts - b.ts);
    return stamped.map((s) => s.row);
  }, [turnOrder, turnsById, summons, subagentResults]);

  const virtualizer = useVirtualizer({
    count: feedRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 178,
    overscan: 4,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const onScroll = (): void => {
      const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      stickyRef.current = distanceFromBottom < STICKY_THRESHOLD_PX;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!stickyRef.current) return;
    const el = parentRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [feedRows.length]);

  // ── Scroll-to-turn IPC listener ───────────────────────────────────
  // Resolves the target id to its index in `turnOrder` (the canonical
  // chronological ordering owned by the stream slice) and asks the
  // virtualizer to centre it. Disengages auto-stick scrolling for
  // ~3 s so the operator's intent isn't immediately overridden by a
  // new turn arriving at the bottom of the rail.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ScrollToTurnDetail>).detail;
      if (!detail?.turnId) return;
      // Feed index'i (turn + summon + subagent karışık) üzerinden çözülür
      // — salt turnOrder.indexOf artık yanlış satıra kayardı.
      const idx = feedRows.findIndex(
        (row) => row.kind === "turn" && row.turnId === detail.turnId,
      );
      if (idx < 0) return;
      stickyRef.current = false;
      virtualizer.scrollToIndex(idx, { align: "center", behavior: "smooth" });
      setHighlightTurnId(detail.turnId);
      // Re-enable auto-stick after the operator's gesture has clearly
      // landed (matches the highlight fade-out window).
      window.setTimeout(() => setHighlightTurnId(null), 1600);
      window.setTimeout(() => {
        // Only re-engage stick if user is now within the threshold —
        // respects manual scroll AFTER the programmatic jump.
        const el = parentRef.current;
        if (!el) return;
        const dist = el.scrollHeight - (el.scrollTop + el.clientHeight);
        stickyRef.current = dist < STICKY_THRESHOLD_PX;
      }, 3000);
    };
    window.addEventListener(CONSENSUS_SCROLL_TURN_EVENT, handler);
    return () => window.removeEventListener(CONSENSUS_SCROLL_TURN_EVENT, handler);
  }, [feedRows, virtualizer]);

  return (
    <Card
      data-transcript-card
      id="live-transcript-panel"
      tone="elevated"
      className="flex h-full flex-col"
      aria-label={t("transcript.title")}
    >
      {/* Premium header — live dot + turn counter */}
      <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2.5">
          {/* Animated broadcast dot */}
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_6px_hsl(0_84%_60%/0.6)]" />
          </span>
          <span className="text-sm font-semibold text-fg-strong tracking-tight">
            Canlı Transkript
          </span>
        </div>
        <div className="flex items-center gap-2">
          {turnOrder.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-[10px] font-mono tabular-nums text-fg-subtle">
              <span className="text-fg-strong font-medium">{turnOrder.length}</span> söz
            </span>
          )}
          {maxRounds > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-[10px] font-mono tabular-nums text-fg-subtle">
              Tur <span className="text-fg-strong font-medium">{Math.max(1, roundIndex)}</span>/{maxRounds}
            </span>
          )}
        </div>
      </div>

      {/* Kalite Planı — kompakt canlı kapsam çubuğu. İlk coverage.update
          frame'ine kadar kendi kendini gizler (null render). */}
      <CoverageLiveBar />
      {/* Kapsam-Güdümlü Soruşturma — canlı hücre defteri çubuğu. İlk
          investigation.plan frame'ine kadar kendi kendini gizler. */}
      <InvestigationLiveBar />

      <div
        ref={parentRef}
        className={
          "relative flex-1 overflow-y-auto px-3 py-3 min-h-0 " +
          "[mask-image:linear-gradient(to_bottom,transparent_0,#000_24px,#000_calc(100%-32px),transparent_100%)]"
        }
        style={{
          // `none` (set by [data-present-mode]) lifts the cap entirely so
          // the transcript fills the right rail; otherwise it follows the
          // viewport-relative clamp().
          maxHeight: "var(--transcript-max-h, clamp(320px, 60vh, 720px))",
        }}
        role="log"
        aria-live="polite"
      >
        {feedRows.length === 0 ? (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-fg-subtle text-balance">{t("transcript.empty")}</p>
            <SkeletonTurn delay={0} />
            <SkeletonTurn delay={120} />
            <SkeletonTurn delay={240} />
          </div>
        ) : (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((vrow) => {
              const row = feedRows[vrow.index];
              if (!row) return null;

              let content: React.ReactNode = null;
              if (row.kind === "turn") {
                const turn = turnsById[row.turnId];
                if (!turn) return null;
                const speaker = personas[turn.speakerId] ?? null;
                content = (
                  <TranscriptTurn
                    turn={turn}
                    persona={speaker}
                    /* isCurrent is OR'd with the transient highlight so a
                       click-to-locate gesture from `BigSpeakerStage`
                       reuses the existing accent ring instead of needing
                       a separate visual treatment. */
                    isCurrent={
                      currentTurnId === row.turnId ||
                      highlightTurnId === row.turnId
                    }
                    index={vrow.index}
                  />
                );
              } else if (row.kind === "summon") {
                content = <ModeratorSummonBand summon={row.summon} />;
              } else {
                content = (
                  <SubagentResultCard
                    kind={row.entry.kind}
                    result={row.entry.result}
                  />
                );
              }

              return (
                <div
                  key={row.key}
                  data-index={vrow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${vrow.start}px)`,
                    paddingBottom: 12,
                  }}
                >
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
